/**
 * src/modules/customer-tags.ts — TAGI KLIENTÓW (0238, 2026-08-16).
 *
 * Rozwinięcie ręcznego pola `status` klienta w tagi (wzorzec tagów zamówień
 * i reklamacji): słownik per firma + chipy na liście/w szufladzie + filtr.
 * NOWOŚĆ: tag może mieć REGUŁĘ AUTO — przypina/zdejmuje się sam na podstawie
 * agregatów klienta (suma zakupów PLN / liczba zamówień / świeżość ostatniego
 * zamówienia). Automat rusza tylko własne przypięcia (added_by='rule:{id}') —
 * tagi nadane ręcznie są święte.
 *
 * Cały kod tagów klientów mieszka TUTAJ; customers/list.ts i customers/drawer.ts
 * wołają wyłącznie eksporty (przez window.__sbCustomerTags — cykl importów).
 */

import { showConfirm } from './sb-dialogs.ts';

type TagKlienta = {
  id: number; name: string; color: string; sort_order: number;
  is_active: boolean; auto_enabled: boolean; auto_rule: any;
};

var _slownik: TagKlienta[] | null = null;
var _slownikCzas = 0;

var KOLORY = ['slate', 'red', 'orange', 'amber', 'green', 'teal', 'blue', 'indigo', 'violet', 'pink'];

function _token(): string {
  try {
    return (window.SellBaseAPI && (window.SellBaseAPI as any).getAuthToken)
      ? (window.SellBaseAPI as any).getAuthToken()
      : (localStorage.getItem('orderflow_token') || sessionStorage.getItem('orderflow_token') || '');
  } catch (_e) { return ''; }
}

function _naglowki(): Record<string, string> {
  return { 'Authorization': 'Bearer ' + _token(), 'Content-Type': 'application/json' };
}

/** fetch NIE rzuca na 4xx/5xx — bez tego guarda każdy błąd HTTP kończył się
 *  toastem sukcesu (review 0238). */
function _sprawdz(r: Response): Response {
  if (!r.ok) throw new Error('HTTP ' + r.status);
  return r;
}

async function pobierzSlownikTagowKlienta(force?: boolean): Promise<TagKlienta[]> {
  var teraz = Date.now();
  if (!force && _slownik && (teraz - _slownikCzas) < 60000) return _slownik;
  try {
    var r = await fetch('/api/customer-tags?all=1', { headers: _naglowki() });
    var d = await r.json();
    _slownik = Array.isArray(d && d.tags) ? d.tags : [];
    _slownikCzas = teraz;
  } catch (_e) { _slownik = _slownik || []; }
  return _slownik || [];
}

function _czyszczSlownik() { _slownik = null; _slownikCzas = 0; }

function _odswiezKlientow() {
  try { var f = (window as any).__sbOdswiezKlientow; if (typeof f === 'function') f(); } catch (_e) {}
}

// ── Chipy ────────────────────────────────────────────────────────────────────

var LIMIT_CHIPOW = 3;

/** Chipy tagów klienta. opts.x = pokaż X (odpinanie), opts.filtr = klik filtruje listę. */
function chipyTagowKlienta(tagi: any[], customerId: string, opts?: { x?: boolean; filtr?: boolean; limit?: number }): string {
  var o = opts || {};
  var lista = Array.isArray(tagi) ? tagi : [];
  if (!lista.length) return '';
  var limit = o.limit === undefined ? LIMIT_CHIPOW : o.limit;
  var widoczne = limit > 0 ? lista.slice(0, limit) : lista;
  var reszta = lista.length - widoczne.length;
  var html = '<span class="ctag-rzad" style="display:inline-flex;gap:3px;flex-wrap:wrap;align-items:center">';
  widoczne.forEach(function(t: any) {
    var auto = String(t.added_by || '').indexOf('rule:') === 0;
    html += '<span class="ptag ptag--' + escapeAttr(t.color || 'slate') + (o.filtr ? ' ctag-chip-filtr' : '') + '"' +
      ' data-ctag-id="' + escapeAttr(String(t.id)) + '"' +
      (o.filtr ? ' style="cursor:pointer"' : '') +
      ' title="' + escapeAttr(t.name + (auto ? ' (automat)' : '')) + '">' +
      (auto ? '<span style="opacity:.7;margin-right:2px">⚙</span>' : '') +
      escapeHtml(t.name) +
      (o.x ? '<span class="ctag-x" data-ctag-customer="' + escapeAttr(customerId) + '" data-ctag-id="' + escapeAttr(String(t.id)) + '" style="cursor:pointer;margin-left:3px;opacity:.65;font-weight:700">×</span>' : '') +
      '</span>';
  });
  if (reszta > 0) html += '<span style="font-size:10px;color:var(--color-text-muted)">+' + reszta + '</span>';
  html += '</span>';
  return html;
}

// ── Wiersz „Tagi" w szufladzie klienta ───────────────────────────────────────

function wierszTagowSzuflady(customer: any): string {
  var tagi = Array.isArray(customer && customer.tags) ? customer.tags : [];
  return '<div class="detail-info-row"><span class="detail-info-label">Tagi</span>' +
    '<span class="detail-info-value" id="cd-tagi-klienta" data-ctag-customer="' + escapeAttr(String(customer.id)) + '" style="display:flex;align-items:center;gap:4px;flex-wrap:wrap">' +
    chipyTagowKlienta(tagi, String(customer.id), { x: true, limit: 0 }) +
    '<button type="button" class="ctag-dodaj" data-ctag-customer="' + escapeAttr(String(customer.id)) + '" style="background:none;border:1px dashed var(--color-border);border-radius:9999px;color:var(--color-text-muted);font-size:10px;padding:1px 8px;cursor:pointer">+ tag</button>' +
    '</span></div>';
}

async function _odswiezWierszSzuflady(customerId: string) {
  var el = document.getElementById('cd-tagi-klienta');
  if (!el || el.getAttribute('data-ctag-customer') !== String(customerId)) return;
  try {
    var r = await fetch('/api/customers/' + encodeURIComponent(customerId), { headers: _naglowki() });
    var d = await r.json();
    var tagi = Array.isArray(d && d.tags) ? d.tags : [];
    el.innerHTML = chipyTagowKlienta(tagi, String(customerId), { x: true, limit: 0 }) +
      '<button type="button" class="ctag-dodaj" data-ctag-customer="' + escapeAttr(String(customerId)) + '" style="background:none;border:1px dashed var(--color-border);border-radius:9999px;color:var(--color-text-muted);font-size:10px;padding:1px 8px;cursor:pointer">+ tag</button>';
  } catch (_e) {}
}

// ── Pasek filtra nad listą klientów ──────────────────────────────────────────

function pasekTagowKlientow() {
  var bar = document.getElementById('customer-tags-bar') as HTMLElement | null;
  if (!bar) return;
  pobierzSlownikTagowKlienta().then(function(tagi) {
    if (!bar) return;
    var aktywne = (tagi || []).filter(function(t) { return t.is_active; });
    var stan: any = (window as any).__sbState || {};
    var wybrany = Number(stan.customersFilterTag || 0);
    // Pasek widoczny ZAWSZE — przycisk „Tagi…" jest jedynym wejściem do
    // słownika; chowanie go przy zerze tagów odcinało możliwość stworzenia
    // pierwszego (zgłoszenie usera 2026-08-16: „nie widzę zakładki tagów").
    var html = '<span style="font-size:10px;color:var(--color-text-muted);font-weight:600;text-transform:uppercase;letter-spacing:.04em;margin-right:2px">Tagi:</span>';
    if (!aktywne.length) {
      html += '<span style="font-size:11px;color:var(--color-text-muted)">brak — dodaj pierwszy w „Tagi…" (mogą przypinać się same, np. „kupił za min. 1000 zł")</span>';
    }
    aktywne.forEach(function(t) {
      var on = wybrany === t.id;
      html += '<span class="ptag ptag--' + escapeAttr(t.color || 'slate') + ' ctag-filtr-chip" data-ctag-id="' + t.id + '"' +
        ' style="cursor:pointer' + (on ? ';outline:2px solid var(--color-primary,#3b82f6);outline-offset:1px' : '') + '"' +
        ' title="' + (on ? 'Zdejmij filtr' : 'Filtruj po tagu') + '">' +
        (t.auto_enabled ? '<span style="opacity:.7;margin-right:2px">⚙</span>' : '') + escapeHtml(t.name) + '</span>';
    });
    html += '<button type="button" id="ctag-zarzadzaj" style="margin-left:auto;background:none;border:1px solid var(--color-border);border-radius:6px;color:var(--color-text-muted);font-size:11px;padding:3px 10px;cursor:pointer">Tagi…</button>';
    bar.style.display = 'flex';
    bar.innerHTML = html;
  });
}

// ── Menu przypinania tagu (szuflada) ─────────────────────────────────────────

async function _pokazMenuTagow(anchor: HTMLElement, customerId: string) {
  var stare = document.getElementById('ctag-menu-popover');
  if (stare) stare.remove();
  var tagi = (await pobierzSlownikTagowKlienta(true)).filter(function(t) { return t.is_active; });
  var kontener = document.getElementById('cd-tagi-klienta');
  var ma: Record<string, boolean> = {};
  if (kontener) {
    kontener.querySelectorAll('[data-ctag-id]').forEach(function(el) {
      ma[String(el.getAttribute('data-ctag-id'))] = true;
    });
  }
  var menu = document.createElement('div');
  menu.id = 'ctag-menu-popover';
  menu.style.cssText = 'position:fixed;z-index:10001;background:var(--color-surface);border:1px solid var(--color-border);border-radius:8px;box-shadow:0 8px 24px rgba(0,0,0,.25);padding:6px;min-width:170px;max-width:calc(100vw - 16px);max-height:260px;overflow:auto';
  var html = '';
  if (!tagi.length) {
    html += '<div style="font-size:11px;color:var(--color-text-muted);padding:6px 8px">Brak tagów — dodaj w „Tagi…" na liście klientów.</div>';
  }
  tagi.forEach(function(t) {
    var zajety = !!ma[String(t.id)];
    html += '<div class="ctag-menu-poz" data-ctag-id="' + t.id + '"' +
      ' style="display:flex;align-items:center;gap:6px;padding:5px 8px;border-radius:6px;font-size:12px;' +
      (zajety ? 'opacity:.45;cursor:default' : 'cursor:pointer') + '"' + (zajety ? ' data-ctag-ma="1"' : '') + '>' +
      '<span class="ptag ptag--' + escapeAttr(t.color || 'slate') + '">' + escapeHtml(t.name) + '</span>' +
      (zajety ? '<span style="margin-left:auto;font-size:10px;color:var(--color-text-muted)">✓</span>' : '') +
      '</div>';
  });
  menu.innerHTML = html;
  document.body.appendChild(menu);
  var r = anchor.getBoundingClientRect();
  // S-160 (MOB-3): pozycja z faktycznej szerokości menu (długie nazwy tagów wychodziły za prawą krawędź).
  menu.style.left = Math.max(8, Math.min(r.left, window.innerWidth - (menu.offsetWidth || 200) - 8)) + 'px';
  menu.style.top = Math.min(r.bottom + 4, window.innerHeight - 280) + 'px';
  menu.addEventListener('click', async function(e: any) {
    var poz = e.target.closest('.ctag-menu-poz');
    if (!poz || poz.getAttribute('data-ctag-ma') === '1') return;
    var tagId = Number(poz.getAttribute('data-ctag-id'));
    menu.remove();
    try {
      _sprawdz(await fetch('/api/customers/' + encodeURIComponent(customerId) + '/tags', {
        method: 'POST', headers: _naglowki(), body: JSON.stringify({ tag_id: tagId }),
      }));
      showToast('Tag przypięty', 'success');
      _odswiezWierszSzuflady(customerId);
      _odswiezKlientow();
    } catch (_e) { showToast('Nie udało się przypiąć tagu', 'error'); }
  });
  setTimeout(function() {
    document.addEventListener('click', function zamknij(e: any) {
      if (!menu.contains(e.target)) { menu.remove(); document.removeEventListener('click', zamknij); }
    });
  }, 0);
}

// ── Modal zarządzania słownikiem (z regułami auto) ───────────────────────────

function _selektorKoloru(wybrany: string, klasa: string): string {
  return '<select class="' + klasa + ' form-select" style="width:86px;font-size:11px;padding:2px 6px">' +
    KOLORY.map(function(k) {
      return '<option value="' + k + '"' + (k === (wybrany || 'slate') ? ' selected' : '') + '>' + k + '</option>';
    }).join('') + '</select>';
}

function _regulaHtml(t: Partial<TagKlienta>): string {
  var r = (t && t.auto_rule) || {};
  var wl = !!(t && t.auto_enabled);
  function pole(klucz: string, etykieta: string, placeholder: string): string {
    var v = r && r[klucz] !== undefined && r[klucz] !== null ? String(r[klucz]) : '';
    return '<label style="display:flex;flex-direction:column;gap:2px;font-size:10px;color:var(--color-text-muted)">' + etykieta +
      '<input type="number" min="0" step="any" class="ctm-rule" data-rule-key="' + klucz + '" value="' + escapeAttr(v) + '" placeholder="' + escapeAttr(placeholder) + '" style="width:110px;font-size:11px;padding:3px 6px" class="form-input"></label>';
  }
  return '<div class="ctm-auto" style="margin-top:6px;padding:8px;border:1px dashed var(--color-border);border-radius:8px">' +
    '<label style="display:inline-flex;align-items:center;gap:6px;font-size:11px;font-weight:600;cursor:pointer">' +
      '<input type="checkbox" class="ctm-auto-on"' + (wl ? ' checked' : '') + '> ⚙ Przypinaj automatycznie, gdy klient spełnia warunki:' +
    '</label>' +
    '<div style="display:flex;gap:10px;flex-wrap:wrap;margin-top:6px">' +
      pole('min_total_pln', 'Kupił za min. (PLN)', 'np. 1000') +
      pole('min_orders', 'Min. liczba zamówień', 'np. 5') +
      pole('last_order_within_days', 'Kupował w ciągu (dni)', 'np. 90') +
      pole('last_order_older_days', 'Nie kupował od (dni)', 'np. 180') +
    '</div>' +
    '<div style="font-size:10px;color:var(--color-text-muted);margin-top:4px">Wypełnione warunki działają ŁĄCZNIE. Automat zdejmuje tylko tagi, które sam przypiął — ręczne zostają. Suma liczona w PLN po kursach NBP ze wszystkich marketplace.</div>' +
  '</div>';
}

function _zbierzRegule(wiersz: HTMLElement): { auto_enabled: boolean; auto_rule: any } {
  var on = !!(wiersz.querySelector('.ctm-auto-on') as any | null)?.checked;
  var rule: any = {};
  wiersz.querySelectorAll('.ctm-rule').forEach(function(inp: any) {
    var v = parseFloat(inp.value);
    if (!isNaN(v) && v > 0) rule[inp.getAttribute('data-rule-key')] = v;
  });
  var maWarunki = Object.keys(rule).length > 0;
  return { auto_enabled: on && maWarunki, auto_rule: maWarunki ? rule : null };
}

async function pokazModalTagowKlientow() {
  var stary = document.getElementById('ctag-modal');
  if (stary) stary.remove();
  var tagi = await pobierzSlownikTagowKlienta(true);

  var overlay = document.createElement('div');
  overlay.id = 'ctag-modal';
  overlay.style.cssText = 'position:fixed;inset:0;z-index:10000;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;padding:20px';

  function wierszHtml(t: TagKlienta): string {
    return '<div class="ctm-row" data-tag-id="' + t.id + '" style="padding:8px;border:1px solid var(--color-border);border-radius:8px;margin-bottom:8px">' +
      '<div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap">' +
        '<span class="ptag ptag--' + escapeAttr(t.color || 'slate') + '">' + escapeHtml(t.name) + '</span>' +
        '<input class="ctm-name form-input" value="' + escapeAttr(t.name) + '" style="flex:1;min-width:120px;font-size:12px;padding:3px 8px">' +
        _selektorKoloru(t.color, 'ctm-color') +
        '<label style="display:inline-flex;align-items:center;gap:4px;font-size:10px;color:var(--color-text-muted);cursor:pointer"><input type="checkbox" class="ctm-active"' + (t.is_active ? ' checked' : '') + '> aktywny</label>' +
        '<button type="button" class="ctm-save btn-sm" style="font-size:10px">Zapisz</button>' +
        '<button type="button" class="ctm-del btn-sm" style="font-size:10px;color:var(--color-error)">Usuń</button>' +
      '</div>' +
      _regulaHtml(t) +
    '</div>';
  }

  var listaHtml = tagi.map(wierszHtml).join('') ||
    '<div style="font-size:12px;color:var(--color-text-muted);padding:8px">Brak tagów — dodaj pierwszy poniżej.</div>';

  overlay.innerHTML =
    '<div style="background:var(--color-surface);border:1px solid var(--color-border);border-radius:12px;max-width:640px;width:100%;max-height:85vh;display:flex;flex-direction:column">' +
      '<div style="display:flex;align-items:center;padding:12px 16px;border-bottom:1px solid var(--color-border)">' +
        '<div style="font-weight:700;font-size:14px">Tagi klientów</div>' +
        '<button type="button" id="ctag-modal-close" style="margin-left:auto;background:none;border:none;color:var(--color-text-muted);font-size:18px;cursor:pointer">✕</button>' +
      '</div>' +
      '<div id="ctag-modal-lista" style="padding:12px 16px;overflow:auto;flex:1">' + listaHtml + '</div>' +
      '<div style="padding:12px 16px;border-top:1px solid var(--color-border)">' +
        '<div style="display:flex;align-items:center;gap:6px">' +
          '<input id="ctag-nowy-nazwa" class="form-input" placeholder="Nowy tag…" style="flex:1;font-size:12px;padding:4px 8px">' +
          _selektorKoloru('slate', 'ctag-nowy-kolor') +
          '<button type="button" id="ctag-nowy-dodaj" class="btn-primary btn-sm" style="font-size:11px">Dodaj</button>' +
        '</div>' +
        '<div style="display:flex;align-items:center;gap:8px;margin-top:10px">' +
          '<button type="button" id="ctag-zastosuj" class="btn-sm" style="font-size:11px">⚙ Zastosuj reguły teraz</button>' +
          '<span id="ctag-zastosuj-wynik" style="font-size:11px;color:var(--color-text-muted)"></span>' +
        '</div>' +
      '</div>' +
    '</div>';
  document.body.appendChild(overlay);

  function zamknij() { overlay.remove(); document.removeEventListener('keydown', naEsc); }
  function naEsc(e: any) { if (e.key === 'Escape') zamknij(); }
  document.addEventListener('keydown', naEsc);
  overlay.addEventListener('click', function(e: any) { if (e.target === overlay) zamknij(); });
  (document.getElementById('ctag-modal-close') as any).onclick = zamknij;

  (document.getElementById('ctag-nowy-dodaj') as any).onclick = async function() {
    var nazwaEl: any = document.getElementById('ctag-nowy-nazwa');
    var kolorEl: any = overlay.querySelector('.ctag-nowy-kolor');
    var nazwa = (nazwaEl && nazwaEl.value || '').trim();
    if (!nazwa) { showToast('Podaj nazwę tagu', 'error'); return; }
    try {
      _sprawdz(await fetch('/api/customer-tags', {
        method: 'POST', headers: _naglowki(),
        body: JSON.stringify({ name: nazwa, color: kolorEl ? kolorEl.value : 'slate' }),
      }));
      _czyszczSlownik();
      showToast('Tag dodany', 'success');
      pokazModalTagowKlientow();
      pasekTagowKlientow();
    } catch (_e) { showToast('Nie udało się dodać tagu', 'error'); }
  };

  (document.getElementById('ctag-zastosuj') as any).onclick = async function() {
    var wynikEl = document.getElementById('ctag-zastosuj-wynik');
    if (wynikEl) wynikEl.textContent = 'Przeliczanie…';
    try {
      var r = _sprawdz(await fetch('/api/customer-tags/apply', { method: 'POST', headers: _naglowki() }));
      var d = await r.json();
      if (wynikEl) wynikEl.textContent = 'Przypięto: ' + (d.added || 0) + ', zdjęto: ' + (d.removed || 0) + '.';
      _odswiezKlientow();
    } catch (_e) { if (wynikEl) wynikEl.textContent = 'Błąd przeliczania.'; }
  };

  var lista = document.getElementById('ctag-modal-lista');
  if (lista) lista.addEventListener('click', async function(e: any) {
    var row = e.target.closest('.ctm-row');
    if (!row) return;
    var tagId = Number(row.getAttribute('data-tag-id'));
    if (e.target.closest('.ctm-save')) {
      var regula = _zbierzRegule(row);
      var dane: any = {
        name: (row.querySelector('.ctm-name') as any).value,
        color: (row.querySelector('.ctm-color') as any).value,
        is_active: (row.querySelector('.ctm-active') as any).checked,
        auto_enabled: regula.auto_enabled,
        auto_rule: regula.auto_rule,
      };
      var autoOn = !!(row.querySelector('.ctm-auto-on') as any)?.checked;
      if (autoOn && !regula.auto_enabled) {
        showToast('Automat wymaga co najmniej jednego warunku', 'error');
        return;
      }
      try {
        var odp = _sprawdz(await fetch('/api/customer-tags/' + tagId, {
          method: 'PUT', headers: _naglowki(), body: JSON.stringify(dane),
        }));
        var dd = await odp.json();
        _czyszczSlownik();
        var doszlo = dd && dd.applied ? (' (auto: +' + (dd.applied.added || 0) + '/−' + (dd.applied.removed || 0) + ')') : '';
        showToast('Tag zapisany' + doszlo, 'success');
        pasekTagowKlientow();
        _odswiezKlientow();
      } catch (_e) { showToast('Nie udało się zapisać', 'error'); }
      return;
    }
    if (e.target.closest('.ctm-del')) {
      try {
        var u = await fetch('/api/customer-tags/' + tagId + '/usage', { headers: _naglowki() }).then(function(r) { return r.json(); });
        var n = (u && u.count) || 0;
        if (!(await showConfirm('Usunąć tag?', n > 0 ? ('Tag jest przypięty do ' + n + ' klientów — zniknie ze wszystkich.') : 'Tag zniknie z listy.', { danger: true }))) return;
        _sprawdz(await fetch('/api/customer-tags/' + tagId, { method: 'DELETE', headers: _naglowki() }));
        _czyszczSlownik();
        showToast('Tag usunięty', 'success');
        pokazModalTagowKlientow();
        pasekTagowKlientow();
        _odswiezKlientow();
      } catch (_e) { showToast('Nie udało się usunąć', 'error'); }
    }
  });
}

// ── Delegacje globalne (chipy w liście/szufladzie, pasek filtra) ─────────────

var _zainstalowane = false;
function zainstalujDelegacjeTagowKlienta() {
  if (_zainstalowane) return;
  _zainstalowane = true;
  document.addEventListener('click', function(e: any) {
    // X na chipie w szufladzie — odpięcie (sprawdzane PRZED filtrem: X leży w chipie).
    var x = e.target.closest('.ctag-x');
    if (x) {
      e.preventDefault(); e.stopPropagation();
      var cid = x.getAttribute('data-ctag-customer');
      var tid = x.getAttribute('data-ctag-id');
      fetch('/api/customers/' + encodeURIComponent(cid) + '/tags/' + tid, {
        method: 'DELETE', headers: _naglowki(),
      }).then(function(r) {
        _sprawdz(r);
        showToast('Tag zdjęty', 'success');
        _odswiezWierszSzuflady(cid);
        _odswiezKlientow();
      }).catch(function() { showToast('Nie udało się zdjąć tagu', 'error'); });
      return;
    }
    // „+ tag" w szufladzie.
    var dodaj = e.target.closest('.ctag-dodaj');
    if (dodaj) {
      e.preventDefault(); e.stopPropagation();
      _pokazMenuTagow(dodaj, dodaj.getAttribute('data-ctag-customer'));
      return;
    }
    // Chip filtra nad listą / chip w wierszu listy.
    var chip = e.target.closest('.ctag-filtr-chip, .ctag-chip-filtr');
    if (chip) {
      e.preventDefault(); e.stopPropagation();
      var stan: any = (window as any).__sbState || {};
      var id = Number(chip.getAttribute('data-ctag-id'));
      stan.customersFilterTag = (Number(stan.customersFilterTag || 0) === id) ? 0 : id;
      pasekTagowKlientow();
      _odswiezKlientow();
      return;
    }
    // Przycisk „Tagi…" na pasku.
    if (e.target.closest('#ctag-zarzadzaj')) {
      e.preventDefault();
      pokazModalTagowKlientow();
    }
  });
}

// ── EXPOSE ───────────────────────────────────────────────────────────────────
if (typeof window !== 'undefined') {
  (window as any).__sbCustomerTags = {
    chipyTagowKlienta: chipyTagowKlienta,
    wierszTagowSzuflady: wierszTagowSzuflady,
    pasekTagowKlientow: pasekTagowKlientow,
    pokazModalTagowKlientow: pokazModalTagowKlientow,
    zainstalujDelegacje: zainstalujDelegacjeTagowKlienta,
  };
  zainstalujDelegacjeTagowKlienta();
}

export {
  chipyTagowKlienta,
  wierszTagowSzuflady,
  pasekTagowKlientow,
  pokazModalTagowKlientow,
  zainstalujDelegacjeTagowKlienta,
};
